import { useCallback, useEffect, useRef, useState } from "react";
import { type Static, type TSchema } from "typebox";
import { PlatformApiError } from "../api/platformApi.ts";
import { usePlatformSession } from "../session/PlatformSessionProvider.tsx";

export type OrganizationResourceStatus = "idle" | "loading" | "ready" | "error";

export interface OrganizationResource<T> {
  readonly organizationId: string | null;
  readonly data: T | null;
  readonly status: OrganizationResourceStatus;
  readonly message: string | null;
  readonly canRetry: boolean;
  reload(): Promise<void>;
  replaceData(data: T): void;
}

export interface UseOrganizationResourceOptions<Schema extends TSchema> {
  readonly path: (organizationId: string) => string;
  readonly responseSchema: Schema;
  /** Changes whenever retained data no longer represents the current view. */
  readonly contextKey?: string;
}

interface ResourceState<T> {
  readonly status: OrganizationResourceStatus;
  readonly data: T | null;
  readonly message: string | null;
  readonly canRetry: boolean;
}

const EMPTY_RESOURCE: ResourceState<never> = {
  status: "idle",
  data: null,
  message: null,
  canRetry: false,
};

function errorDetails(error: unknown): Pick<ResourceState<never>, "message" | "canRetry"> {
  if (error instanceof PlatformApiError) {
    return {
      message: error.message,
      canRetry: error.retryable || error.status === 409 || error.status >= 500,
    };
  }
  return { message: "This workspace could not be loaded.", canRetry: true };
}

/**
 * Reads an active-organization view with an abort and request-version fence so
 * a response from a previous organization cannot replace the current screen.
 */
export function useOrganizationResource<Schema extends TSchema>({
  path,
  responseSchema,
  contextKey: viewContextKey = "",
}: UseOrganizationResourceOptions<Schema>): OrganizationResource<Static<Schema>> {
  const session = usePlatformSession();
  const organizationId = session.identity?.activeOrganizationId ?? null;
  const [state, setState] = useState<ResourceState<Static<Schema>>>(EMPTY_RESOURCE);
  const requestVersion = useRef(0);
  const requestController = useRef<AbortController | null>(null);
  const contextKey = useRef<string | null>(null);

  const reload = useCallback(async () => {
    if (session.phase !== "ready" || !organizationId) {
      requestVersion.current += 1;
      requestController.current?.abort();
      requestController.current = null;
      contextKey.current = null;
      setState(EMPTY_RESOURCE);
      return;
    }

    const nextContextKey = `${session.identity?.platformSessionId ?? ""}:${organizationId}:${viewContextKey}`;
    const contextChanged = contextKey.current !== nextContextKey;
    contextKey.current = nextContextKey;
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setState((current) => ({
      status: "loading",
      data: contextChanged ? null : current.data,
      message: null,
      canRetry: false,
    }));

    try {
      const result = await session.api.get({
        path: path(organizationId),
        responseSchema,
        signal: controller.signal,
      });
      if (requestVersion.current !== version || controller.signal.aborted) return;
      setState({ status: "ready", data: result.data, message: null, canRetry: false });
    } catch (error) {
      if (requestVersion.current !== version || controller.signal.aborted) return;
      const details = errorDetails(error);
      setState((current) => ({ status: "error", data: current.data, ...details }));
    } finally {
      if (requestController.current === controller) requestController.current = null;
    }
  }, [organizationId, path, responseSchema, session.api, session.identity?.platformSessionId, session.phase, viewContextKey]);

  useEffect(() => {
    void reload();
    return () => {
      requestVersion.current += 1;
      requestController.current?.abort();
      requestController.current = null;
    };
  }, [reload]);

  const replaceData = useCallback((data: Static<Schema>) => {
    requestVersion.current += 1;
    setState({ status: "ready", data, message: null, canRetry: false });
  }, []);

  return {
    organizationId,
    data: state.data,
    status: state.status,
    message: state.message,
    canRetry: state.canRetry,
    reload,
    replaceData,
  };
}
